import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Section } from '@/components/primitives/Section';
import { MobileFormFooter } from '@/components/primitives/MobileFormFooter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { getFirefighter, updateFirefighter } from '@/api/firefighters';
import { useAuth } from '@/contexts/AuthContext';
import {
  FirefighterSchema,
  type FirefighterValues,
} from './FirefighterSchema';

interface FirefighterRecord {
  id: number;
  name: string;
  surname: string;
  note?: string | null;
  contact?: string | null;
  address?: string | null;
  emergency_contact?: string | null;
  medical_aid_details?: string | null;
}

export default function FirefighterEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const firefighterId = id ? Number(id) : NaN;
  const { can } = useAuth();
  // A non-admin is served these as null, so if the fields rendered they
  // would show blank and a save could wipe real values.
  const canSeePersonal = can('firefighter', 'delete');

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [original, setOriginal] = useState<FirefighterRecord | null>(null);

  const form = useForm<FirefighterValues>({
    resolver: zodResolver(FirefighterSchema),
    defaultValues: { name: '', surname: '', note: '', contact: '', address: '', emergency_contact: '', medical_aid_details: '' },
    mode: 'onTouched',
  });

  // Pull the current record once and seed the form with it. Bad/missing ids
  // surface as an inline error rather than a blank form so the user knows the
  // edit didn't load.
  useEffect(() => {
    let cancelled = false;
    if (!Number.isFinite(firefighterId)) {
      setError('Invalid firefighter id.');
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = (await getFirefighter(firefighterId)) as FirefighterRecord;
        if (cancelled) return;
        setOriginal(data);
        form.reset({
          name: data.name ?? '',
          surname: data.surname ?? '',
          note: data.note ?? '',
          contact: data.contact ?? '',
          address: data.address ?? '',
          emergency_contact: data.emergency_contact ?? '',
          medical_aid_details: data.medical_aid_details ?? '',
        });
      } catch (err) {
        console.error('Failed to load firefighter:', err);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = err as any;
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? 'Firefighter not found.'
              : e?.response?.data?.detail || e?.message || 'Failed to load firefighter',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // form.reset is stable from RHF, intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firefighterId]);

  const onSubmit = async (values: FirefighterValues) => {
    if (!Number.isFinite(firefighterId)) return;
    setError(null);
    setSubmitting(true);
    try {
      await updateFirefighter(firefighterId, {
        name: values.name,
        surname: values.surname,
        note: values.note || null,
        // Omitted entirely when the user cannot see them. Sending null would
        // be a backend no-op anyway, but not sending is the honest signal.
        ...(canSeePersonal
          ? {
              contact: values.contact || null,
              address: values.address || null,
              emergency_contact: values.emergency_contact || null,
              medical_aid_details: values.medical_aid_details || null,
            }
          : {}),
      });
      toast.success(`${values.name} ${values.surname} updated`);
      navigate('/firefighters');
    } catch (err) {
      console.error('Update firefighter failed:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setError(e?.response?.data?.detail || e?.message || 'Failed to update firefighter');
    } finally {
      setSubmitting(false);
    }
  };

  const title = original
    ? `Edit ${original.name} ${original.surname}`
    : 'Edit firefighter';

  return (
    <PageShell>
      <PageHeader
        title={title}
        breadcrumbs={[
          { label: 'Firefighters', to: '/firefighters' },
          { label: original ? `${original.name} ${original.surname}` : 'Edit' },
        ]}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading firefighter…
        </div>
      ) : !original ? (
        <Alert variant="destructive">
          <AlertDescription>{error ?? 'Firefighter not found.'}</AlertDescription>
        </Alert>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pb-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Section title="Identity">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        First name <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="given-name" disabled={submitting} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="surname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Surname <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="family-name" disabled={submitting} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Section>

            {canSeePersonal && (
          <Section
            title="Personal details"
            description="Reference only, not used anywhere else in the app yet. Visible to FPA administrators."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="contact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact number</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode="tel" autoComplete="tel" disabled={submitting} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emergency_contact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency contact</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Name and number" disabled={submitting} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} disabled={submitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="medical_aid_details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Medical aid details</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} placeholder="Scheme, plan and member number" disabled={submitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>
          )}
          <Section title="Notes" description="Optional internal notes.">
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="Any details about training, role, or contact."
                        disabled={submitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Section>

            {/* Desktop inline actions */}
            <div className="hidden justify-end gap-2 md:flex">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/firefighters')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save changes'}
              </Button>
            </div>

            <MobileFormFooter
              isSubmitting={submitting}
              cancelTo="/firefighters"
              saveLabel="Save changes"
            />
          </form>
        </Form>
      )}
    </PageShell>
  );
}
